/**
 * Shows calculation details and optional route map for Containervervoer plaats van bestemming.
 * Renders below the delivery address when Seafreight FCL is selected.
 * Uses POST to fetch map (bypasses URL length limits).
 */
import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatEuro } from "../BookingWizard/PricingCalculatorHelpers";
import { fetchRouteMapBlob } from "../../utils/staticMapUtils";

const { Text } = Typography;

export interface ContainervervoerCalculationInfoProps {
  portName: string | null;
  destLabel: string | null;
  distanceKm: number | null;
  unknownKm: boolean;
  sellingPrice: number;
  /** Formula for display, e.g. "€890 + (1069 − 300) km × €1,50" */
  calculationFormula?: string | null;
  portCoords: { lat: number; lon: number } | null;
  destCoords: { lat: number; lon: number } | null;
  routeGeometry: number[][];
  /** True when we have a valid route for current selection (storage or delivery with address) */
  routeReady?: boolean;
  /** Pre-generated map image path for instant display (e.g. storage routes) */
  preloadedMapSrc?: string | null;
}

export const ContainervervoerCalculationInfo: React.FC<
  ContainervervoerCalculationInfoProps
> = ({
  portName,
  destLabel,
  distanceKm,
  unknownKm,
  sellingPrice,
  calculationFormula,
  portCoords,
  destCoords,
  routeGeometry,
  routeReady = true,
  preloadedMapSrc,
}) => {
  const { t } = useTranslation();
  const [mapBlobUrl, setMapBlobUrl] = useState<string | null>(null);
  const [mapLoading, setMapLoading] = useState(false);

  const hasCoords = portCoords && destCoords;
  const hasRoute = routeGeometry.length >= 2;
  /** Only show map when we have a valid route for current selection and distance calculated */
  const hasValidRoute = routeReady && distanceKm != null && !unknownKm;

  const mapKey =
    hasCoords && hasValidRoute
      ? `${portCoords.lat},${portCoords.lon}-${destCoords.lat},${destCoords.lon}-${hasRoute ? routeGeometry.length : 0}`
      : "";

  useEffect(() => {
    if (!hasCoords || !hasValidRoute) {
      setMapBlobUrl(null);
      setMapLoading(false);
      return;
    }

    if (preloadedMapSrc) {
      setMapBlobUrl(preloadedMapSrc);
      setMapLoading(false);
      return;
    }

    let cancelled = false;
    setMapLoading(true);

    fetchRouteMapBlob(
      portCoords,
      destCoords,
      hasRoute ? routeGeometry : undefined,
    )
      .then((blobUrl) => {
        if (!cancelled && blobUrl) {
          setMapBlobUrl(blobUrl);
        }
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });

    return () => {
      cancelled = true;
      setMapBlobUrl((prev) => {
        if (prev && !prev.startsWith("/")) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [mapKey, hasValidRoute, preloadedMapSrc]);

  return (
    <div
      style={{
        marginTop: 8,
        padding: 8,
        background: "#fafafa",
        borderRadius: 4,
        border: "1px solid #f0f0f0",
      }}
    >
      <div style={{ marginBottom: 6 }}>
        <Text strong style={{ fontSize: 14 }}>
          {t("tools.calculator.containervervoerInfo", "Containervervoer berekening")}
        </Text>
      </div>

      {portName && destLabel && (
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
          {t("tools.calculator.route", "Route")}: {portName} → {destLabel}
        </Text>
      )}

      {unknownKm ? (
        <Text type="secondary" style={{ fontSize: 13, display: "block" }}>
          {t("tools.calculator.unknownKm", "Onbekende kilometers")}
        </Text>
      ) : (
        <>
          {distanceKm != null && (
            <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
              {t("tools.calculator.distance", "Afstand")}: {distanceKm.toFixed(0)} km
            </Text>
          )}
          {calculationFormula && (
            <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
              {t("tools.calculator.calculation", "Berekening")}: {calculationFormula}
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 13, display: "block" }}>
            {t("tools.calculator.pricing", "Prijs")}: {formatEuro(sellingPrice)}
          </Text>
        </>
      )}

      {hasCoords && hasValidRoute && (
        <div
          style={{
            marginTop: 8,
            width: "100%",
            maxWidth: 400,
            aspectRatio: "4/3",
            minHeight: 180,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid #e8e8e8",
          }}
        >
          {mapLoading && (
            <div
              style={{
                width: "100%",
                height: "100%",
                minHeight: 180,
                background: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                color: "#999",
              }}
            >
              {t("tools.calculator.loadingMap", "Kaart laden...")}
            </div>
          )}
          {mapBlobUrl && !mapLoading && (
            <img
              src={mapBlobUrl}
              alt={t("tools.calculator.routeMap", "Route map")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
          {!mapLoading && !mapBlobUrl && (
            <div
              style={{
                width: "100%",
                height: "100%",
                minHeight: 180,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                color: "#999",
              }}
            >
              {t("tools.calculator.mapUnavailable", "Kaart niet beschikbaar")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
